import { GameMode } from './GameTypes';

// Base analytics event interface - session_id and timestamp are added automatically by AnalyticsService
export interface BaseAnalyticsEvent {
  event_name: string;
  timestamp?: number;
  session_id?: string;
}

// Core game events
export interface GameStartEvent extends BaseAnalyticsEvent {
  event_name: 'game_start';
  game_mode: GameMode;
  word_length: number;
}

export interface GameCompleteEvent extends BaseAnalyticsEvent {
  event_name: 'game_complete';
  success: boolean;
  score: number;
  attempts: number;
  time_duration: number;
  perfect_game: boolean;
}

// User engagement events
export interface DailyChallengeStartEvent extends BaseAnalyticsEvent {
  event_name: 'daily_challenge_start';
  date: string;
  time: string;
}

export interface DailyChallengeCompleteEvent extends BaseAnalyticsEvent {
  event_name: 'daily_challenge_complete';
  final_score: number;
  max_possible_score: number;
  date: string;
  time: string;
}

export interface SettingsChangeEvent extends BaseAnalyticsEvent {
  event_name: 'settings_change';
  setting_name: string;
  old_value: string;
  new_value: string;
  user_type: 'new' | 'returning';
}

export interface ModeSwitchEvent extends BaseAnalyticsEvent {
  event_name: 'mode_switch';
  from_mode: GameMode;
  to_mode: GameMode;
  trigger_source: 'button' | 'navigation';
}

// User experience events
export interface PageViewEvent extends BaseAnalyticsEvent {
  event_name: 'page_view';
  game_mode?: GameMode;
  user_session_count: number;
  time_since_last_visit?: number;
}

export interface UserEngagementEvent extends BaseAnalyticsEvent {
  event_name: 'user_engagement';
  session_duration: number;
  games_per_session: number;
  return_visitor: boolean;
}

export interface ErrorEncounteredEvent extends BaseAnalyticsEvent {
  event_name: 'error_encountered';
  error_type: string;
  error_context: string;
  user_action_taken?: string;
}

export interface PerformanceIssueEvent extends BaseAnalyticsEvent {
  event_name: 'performance_issue';
  metric_name: 'LCP' | 'FID' | 'CLS' | 'custom';
  value: number;
  threshold_exceeded: boolean;
}

// Conversion/retention events
export interface FirstGameCompleteEvent extends BaseAnalyticsEvent {
  event_name: 'first_game_complete';
  time_to_first_completion: number;
}

export interface StreakMilestoneEvent extends BaseAnalyticsEvent {
  event_name: 'streak_milestone';
  streak_length: number;
}

export interface ReturnVisitEvent extends BaseAnalyticsEvent {
  event_name: 'return_visit';
  days_since_last_visit: number;
  session_count: number;
  total_games_played: number;
}

export interface ModalOpenEvent extends BaseAnalyticsEvent {
  event_name: 'modal_open';
  modal_type: 'settings' | 'how_to_play' | 'results';
  user_type: 'new' | 'returning';
}

// Sharing events - critical for NPS measurement
export interface ShareEvent extends BaseAnalyticsEvent {
  event_name: 'share_clipboard' | 'share_twitter' | 'share_facebook' | 'share_whatsapp';
  platform: string;
  daily_score: number;
  streak_length: number;
  successful: boolean;
  user_type: 'new' | 'returning';
}

// Union type for all analytics events
export type AnalyticsEvent = 
  | GameStartEvent
  | GameCompleteEvent
  | DailyChallengeStartEvent
  | DailyChallengeCompleteEvent
  | SettingsChangeEvent
  | ModeSwitchEvent
  | PageViewEvent
  | UserEngagementEvent
  | ErrorEncounteredEvent
  | PerformanceIssueEvent
  | FirstGameCompleteEvent
  | StreakMilestoneEvent
  | ReturnVisitEvent
  | ModalOpenEvent
  | ShareEvent;

// Configuration interfaces
export interface AnalyticsConfig {
  measurementId: string;
  enableInDevelopment: boolean;
  enableInProduction: boolean;
  enableInTest: boolean;
  batchEvents: boolean;
  batchSize: number;
  batchTimeout: number;
  debugMode: boolean;
}

export interface PrivacyConfig {
  anonymizeIP: boolean;
  respectDoNotTrack: boolean;
  enableUserOptOut: boolean;
  cookieConsent: boolean;
}

// Service interfaces
export interface IAnalyticsService {
  initialize(config?: AnalyticsConfig): Promise<void>;
  trackEvent<T extends AnalyticsEvent>(event: T): void;
  trackGameEvent(event: GameStartEvent | GameCompleteEvent): void;
  trackUserBehavior(event: UserEngagementEvent | SettingsChangeEvent | ModeSwitchEvent): void;
  trackPerformance(event: PerformanceIssueEvent): void;
  trackConversion(event: FirstGameCompleteEvent | StreakMilestoneEvent): void;
  isInitialized(): boolean;
  setUserProperty(property: string, value: string | number | boolean): void;
  flush(): Promise<void>;
}

// Analytics error class
export class AnalyticsError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly context?: any
  ) {
    super(message);
    this.name = 'AnalyticsError';
  }
}

// Session tracking interface
export interface AnalyticsSession {
  id: string;
  startTime: number;
  lastActivity: number;
  eventCount: number;
  gameCount: number;
}

// User properties interface
export interface UserProperties {
  user_type: 'new' | 'returning';
  session_count: number;
  total_games_played: number;
  preferred_game_mode?: GameMode;
  preferred_word_length?: number;
  current_streak?: number;
}

// Performance metric interface
export interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType?: string;
}

// Event queue interface for batching
export interface EventQueue {
  events: AnalyticsEvent[];
  lastFlush: number;
  size: number;
}